/**
 * MILESTONE 2 LEFT PANEL — the monolith's block palette, set up the way Jack built it
 * (index.html L17566–17569 markup, L12967–13031 esPal/chip/esChipPreview, L515–620 CSS):
 *
 *   ┌ TEMPLATE  <name>  ▾ ┐   navy template selector
 *   All · Header · Copy · Homes · … · Template      scrolling .eskind tabs
 *   [ Search blocks & modules… ]
 *   GROUP HEAD
 *   [ico]  Block name                     👁        .eschipm + eye hover preview
 *
 * One chip per block FAMILY (m2Blocks.M2_FAMILIES) — the variant is picked in the right
 * panel's strip, the way the CTA row already picks one button or two.
 *
 * Pure presentation: every action is a callback, so the Studio keeps one source of truth
 * for the document and the placement rules.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { blockDef } from "../email/blocks";
import { iconKey, PAL_TABS, paletteView, type IconKey, type PalGroup } from "../services/m2Blocks";

/* the monolith's IC glyphs, verbatim (index.html L8097–8115) */
const IC: Record<IconKey, string> = {
  logo: '<rect x="4" y="9" width="26" height="8" rx="1.5" fill="#1B365D"/>',
  img: '<rect x="3" y="3" width="28" height="20" rx="2" fill="#c9d3e2"/><circle cx="11" cy="10" r="2.5" fill="#fff"/><path d="M6 20l7-6 6 5 4-3 4 4v2H6z" fill="#8ba0c0"/>',
  headline: '<rect x="5" y="10" width="24" height="6" rx="1.5" fill="#1B365D"/>',
  body: '<rect x="5" y="8" width="24" height="3" rx="1.5" fill="#9aa0a8"/><rect x="5" y="13" width="24" height="3" rx="1.5" fill="#9aa0a8"/><rect x="5" y="18" width="16" height="3" rx="1.5" fill="#9aa0a8"/>',
  btn: '<rect x="8" y="9" width="18" height="8" rx="4" fill="#1B2A58"/>',
  card: '<rect x="6" y="4" width="20" height="18" rx="2" fill="#c9d3e2"/><rect x="6" y="4" width="20" height="9" fill="#8ba0c0"/><rect x="9" y="15" width="12" height="2.5" rx="1" fill="#5a6472"/>',
  table: '<rect x="4" y="6" width="24" height="14" rx="1.5" fill="#e7e3d9"/><rect x="4" y="6" width="24" height="4" fill="#1B2A58"/><line x1="12" y1="10" x2="12" y2="20" stroke="#c9c3b5"/><line x1="20" y1="10" x2="20" y2="20" stroke="#c9c3b5"/>',
  grid: '<rect x="5" y="5" width="9" height="7" rx="1" fill="#1B365D"/><rect x="18" y="5" width="9" height="7" rx="1" fill="#4A9B9B"/><rect x="5" y="15" width="9" height="7" rx="1" fill="#4A9B9B"/><rect x="18" y="15" width="9" height="7" rx="1" fill="#1B365D"/>',
  map: '<rect x="4" y="5" width="26" height="16" rx="2" fill="#dfe6ef"/><path d="M17 8a3 3 0 013 3c0 2-3 5-3 5s-3-3-3-5a3 3 0 013-3z" fill="#b23b3b"/>',
  event: '<rect x="5" y="6" width="22" height="16" rx="2" fill="#1B2A58"/><rect x="5" y="6" width="22" height="5" fill="#C8A664"/>',
  person: '<circle cx="11" cy="11" r="4" fill="#1B365D"/><rect x="18" y="9" width="10" height="2.5" rx="1" fill="#9aa0a8"/><rect x="18" y="14" width="7" height="2.5" rx="1" fill="#c7c2b6"/>',
  divider: '<rect x="4" y="12" width="24" height="2" rx="1" fill="#c9c3b5"/>',
  footer: '<rect x="4" y="8" width="24" height="12" rx="2" fill="#1B2A58"/><circle cx="11" cy="14" r="1.6" fill="#8ba0c0"/><circle cx="16" cy="14" r="1.6" fill="#8ba0c0"/><circle cx="21" cy="14" r="1.6" fill="#8ba0c0"/>',
};
const icoHtml = (k: IconKey) => ({ __html: '<svg width="34" height="26" viewBox="0 0 34 26">' + IC[k] + "</svg>" });

/** the monolith eye (EYE_SVG, L13013) */
function Eye() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** a block's default render, as the monolith's hover preview drew it (esChipPreview) */
function previewHtml(type: string): string {
  try {
    const def = blockDef(type);
    if (!def) return "";
    const row = (def.render as (d: unknown) => string)(JSON.parse(JSON.stringify(def.def())));
    return '<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;border-collapse:collapse;background:#fff"><tbody>' + row + "</tbody></table>";
  } catch {
    return "";
  }
}

/** the floating preview next to the eye — fixed-position, so it is never clipped by the panel */
function ChipPreview({ type, label, anchor }: { type: string; label: string; anchor: DOMRect }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const pv = ref.current;
    if (!pv) return;
    const sc = pv.querySelector(".scaler") as HTMLElement | null;
    const h = sc ? sc.scrollHeight * 0.4333 : 0;
    const top = Math.min(anchor.top, window.innerHeight - pv.offsetHeight - 12);
    let left = anchor.right + 10;
    if (left + pv.offsetWidth > window.innerWidth - 8) left = anchor.left - pv.offsetWidth - 10;
    setPos({ top: Math.max(8, top), left: Math.max(8, left), h });
  }, [type, anchor]);
  return (
    <div className="eschipprev" ref={ref} data-testid="m2-preview" style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}>
      <div className="cap">{label}</div>
      <div className="frame" style={pos && pos.h ? { height: pos.h } : undefined}>
        <div className="scaler" dangerouslySetInnerHTML={{ __html: previewHtml(type) }} />
      </div>
    </div>
  );
}

export interface M2PaletteProps {
  groups: PalGroup[];
  /** the open template's display name */
  tplName: string;
  /** the open template's block names, in template order — the Template tab */
  tplOrder: string[];
  /** every template the library declares — the selector's list */
  templates: string[];
  onPickTemplate?: (name: string) => void;
  /* Blank canvas and All blocks were removed 2026-10-26 (Jack): every email starts from a
     template, so the panel only ever adds to the template that is open. */
  q: string;
  setQ: (q: string) => void;
  onAdd: (type: string) => void;
  onDragType: (type: string | null) => void;
}

const PAL_MIN = 220, PAL_MAX = 520, PAL_DEF = 312; /* monolith wireResize("esResL",…,312,220,520,"esPalW") */

export default function M2Palette(p: M2PaletteProps) {
  const [tab, setTab] = useState("all");
  const [tplOpen, setTplOpen] = useState(false);
  const [hover, setHover] = useState<{ type: string; label: string; rect: DOMRect } | null>(null);
  const [w, setW] = useState<number>(() => {
    try { const v = parseInt(localStorage.getItem("esPalW") || "", 10); return v >= PAL_MIN && v <= PAL_MAX ? v : PAL_DEF; } catch { return PAL_DEF; }
  });
  const drag = useRef<{ x: number; w: number } | null>(null);
  const resStart = (e: React.MouseEvent) => {
    e.preventDefault();
    drag.current = { x: e.clientX, w };
    const mm = (ev: MouseEvent) => { const d = drag.current; if (d) setW(Math.max(PAL_MIN, Math.min(PAL_MAX, d.w + (ev.clientX - d.x)))); };
    const mu = () => {
      drag.current = null;
      document.removeEventListener("mousemove", mm); document.removeEventListener("mouseup", mu);
      document.body.style.userSelect = ""; document.body.style.cursor = "";
      setW((v) => { try { localStorage.setItem("esPalW", String(v)); } catch { /* quota */ } return v; });
    };
    document.addEventListener("mousemove", mm); document.addEventListener("mouseup", mu);
    document.body.style.userSelect = "none"; document.body.style.cursor = "col-resize";
  };

  const view = paletteView(p.groups, tab, p.tplOrder, p.tplName);

  return (
    <>
      <aside className="espal m2pal" data-testid="m2-palette" style={{ width: w }}>
        <div className="estplbar">
          <button type="button" className="estplsel" data-testid="m2-tplsel" onClick={() => setTplOpen((o) => !o)}>
            <span style={{ flex: 1, overflow: "hidden" }}>
              <span className="lbl">Template</span><br />
              <span className="nm">{p.tplName || "Choose a template"}</span>
            </span>
            <span className="cv">&#9662;</span>
          </button>
          {tplOpen && (
            <div className="m2tplmenu" data-testid="m2-tplmenu">
              {p.templates.map((t) => (
                <button type="button" key={t} className={t === p.tplName ? "on" : undefined}
                  onClick={() => { setTplOpen(false); if (p.onPickTemplate) p.onPickTemplate(t); }}>{t}</button>
              ))}
            </div>
          )}
        </div>
        <div className="eskind" data-testid="m2-tabs">
          {PAL_TABS.map((t) => (
            <button type="button" key={t.id} data-k={t.id} className={tab === t.id ? "on" : undefined} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
        <input className="essearch" placeholder="Search blocks & modules…" value={p.q} onChange={(e) => p.setQ(e.target.value)} data-testid="m2-search" />
        <div className="m2list">
          {view.length === 0 ? (
            <div className="espal-empty">{p.q ? "No matches." : tab === "__tpl" ? "No template blocks found in SFMC yet." : "Getting the blocks from Marketing Cloud…"}</div>
          ) : view.map((g) => (
            <div key={g.id}>
              <div className={"esgh" + (g.id === "__tpl" ? " rec" : "")}>{g.title}</div>
              {g.items.map((it) => (
                <div key={it.type} className={"eschipm" + (it.off ? " off" : "")} draggable={!it.off} aria-disabled={it.off ? true : undefined} data-testid="m2-chip" data-type={it.type} data-members={(it.members || [it.name]).join(",")} title={it.off || it.name}
                  onDragStart={(e) => { setHover(null); p.onDragType(it.type); e.dataTransfer.effectAllowed = "copy"; try { e.dataTransfer.setData("text/plain", it.type); } catch { /* jsdom */ } }}
                  onDragEnd={() => p.onDragType(null)}
                  onClick={(e) => { if ((e.target as HTMLElement).closest("[data-eye]")) return; p.onAdd(it.type); }}>
                  <span className="ico" dangerouslySetInnerHTML={icoHtml(iconKey(it.name))} />
                  <span className="nm">{it.label}</span>
                  <button type="button" className="eschipeye" data-eye={it.type} title="Preview this block"
                    onMouseEnter={(e) => setHover({ type: it.type, label: it.label, rect: e.currentTarget.getBoundingClientRect() })}
                    onMouseLeave={() => setHover(null)}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
                    <Eye />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </aside>
      <div className="esresize" title="Drag to resize — double-click to reset" data-testid="m2-resize"
        onMouseDown={resStart}
        onDoubleClick={() => { setW(PAL_DEF); try { localStorage.removeItem("esPalW"); } catch { /* noop */ } }} />
      {hover && <ChipPreview type={hover.type} label={hover.label} anchor={hover.rect} />}
    </>
  );
}
